import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Link2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SkuPicker, useSkuOptions } from '../components/sales/SalesCodeMatching';
import { CodeSuggestions } from '../components/CodeSuggestion';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { SortHeader, TablePager, useTableControls } from '../components/TableControls';
import { MatchingStatsStrip } from '../components/MatchingStatsStrip';
import { MappingQueue } from '../components/MappingQueue';
import { canDoAdminAction, canEditModule } from '../lib/permissions';
import { downloadFile } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENT_MS = 7 * 24 * 3600 * 1000;
const SOURCE_LABELS = { sales_upload: 'Sales upload', sku_master_upload: 'Master file', upload: 'File upload', manual: 'Manual', code_mappings_page: 'Manual', auto: 'PO auto-match', auto_learned: 'Learned from PO import', auto_rule: 'Matching rule', suggestion_accepted: 'Accepted suggestion' };
const sourceLabel = (source) => SOURCE_LABELS[source] || (source?.startsWith('MASTER_OLD_CODE') ? 'Master file' : source || '—');
const SEARCH_KEYS = ['alias', 'sku_code', 'product_name', 'created_by', 'updated_by'];
const INITIAL_SORT = { key: 'alias', dir: 'asc' };
const SORT_VALUE = {
  source: (r) => sourceLabel(r.source),
  created_at: (r) => (r.created_at ? new Date(r.created_at).getTime() : null),
  updated_at: (r) => (r.updated_at ? new Date(r.updated_at).getTime() : null),
};
const shortDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const MappingDialog = ({ mapping, open, onOpenChange, country, skus, onSaved }) => {
  const [code, setCode] = useState('');
  const [sku, setSku] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setCode(mapping?.alias || ''); setSku(mapping?.sku_code || ''); }
  }, [open, mapping]);

  const save = async () => {
    setSaving(true);
    try {
      if (mapping) await axios.put(`${API}/sku-aliases/${mapping.id}`, { alias: code, sku_code: sku, country }, { headers: getAuthHeaders() });
      else await axios.post(`${API}/sku-aliases`, { alias: code, sku_code: sku, source: 'code_mappings_page', country }, { headers: getAuthHeaders() });
      toast.success(mapping ? 'Mapping updated' : 'Mapping added');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="code-mapping-dialog">
        <DialogHeader><DialogTitle>{mapping ? 'Edit code mapping' : 'Add code mapping'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-gray-600" htmlFor="mapping-code">Code used in files (exact)</label>
          <Input id="mapping-code" value={code} onChange={(e) => setCode(e.target.value)} className="font-mono" data-testid="code-mapping-code-input" />
          {!mapping && <CodeSuggestions code={code} country={country} onPick={setSku} />}
          <span className="block text-xs font-medium text-gray-600">Maps to SKU or bundle</span>
          <SkuPicker code="dialog" value={sku} skus={skus} onPick={setSku} disabled={saving} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !code.trim() || !sku} data-testid="code-mapping-save">{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MappingRow = ({ row, canEdit, canDelete, onEdit, onDelete }) => (
  <tr className="border-t text-[13px]" data-testid={`code-mapping-row-${row.alias}`}>
    <td className="p-3 font-mono font-semibold">{row.alias}</td>
    <td className="p-3">
      <div className="font-mono">{row.sku_code}{row.sku_missing && <span className="ml-2 text-[11px] text-red-600">SKU deleted</span>}</div>
      <div className="max-w-xs truncate text-[11px] text-gray-500">{row.product_name}</div>
      {row.bundle_of && <div className="text-[10px] font-semibold text-emerald-700">Bundle · {row.bundle_qty} × {row.bundle_of}</div>}
    </td>
    <td className="p-3 text-gray-600">{sourceLabel(row.source)}</td>
    <td className="p-3 text-gray-600">{row.created_by || '—'}<div className="text-[11px] text-gray-500">{shortDate(row.created_at)}</div></td>
    <td className="p-3 text-gray-600">{row.updated_by ? <>{row.updated_by}<div className="text-[11px] text-gray-500">{shortDate(row.updated_at)}</div></> : '—'}</td>
    <td className="whitespace-nowrap p-3 text-right">
      {canEdit && <Button size="icon" variant="ghost" onClick={() => onEdit(row)} aria-label={`Edit ${row.alias}`} title={`Edit ${row.alias}`} data-testid={`code-mapping-edit-${row.alias}`}><Pencil className="h-4 w-4" aria-hidden="true" /></Button>}
      {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(row)} aria-label={`Delete ${row.alias}`} title={`Delete ${row.alias}`} data-testid={`code-mapping-delete-${row.alias}`}><Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" /></Button>}
      {!canEdit && !canDelete && <span className="text-[11px] text-gray-500">View only</span>}
    </td>
  </tr>
);

const UploadMappingsButton = ({ country, onDone }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/sku-aliases/upload-file?country=${country}`, form, { headers: getAuthHeaders() });
      const { created = 0, updated = 0, invalid = [], moved_sales: movedSales = 0, matched_returns: matchedReturns = 0, matched_po_lines: matchedPo = 0 } = res.data;
      const ignored = res.data.ignored || [];
      const moved = movedSales + matchedReturns + matchedPo ? ` · ${movedSales} sales line(s), ${matchedReturns} return row(s), ${matchedPo} PO line(s) moved in` : '';
      const extra = ignored.length ? ` · ${ignored.length} code(s) ignored` : '';
      const show = invalid.length ? toast.warning : toast.success;
      show(`Saved ${created + updated} mapping(s)${moved}${extra}`, { description: invalid.length ? `${invalid.length} held back or skipped: ${invalid.slice(0, 4).map((i) => `${i.alias} (${i.reason})`).join(', ')}${invalid.length > 4 ? ' …' : ''}` : undefined });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="h-9 text-[13px]" data-testid="code-mapping-upload-label">
        <Upload className="mr-2 h-4 w-4" aria-hidden="true" />{busy ? 'Uploading…' : 'Upload filled file'}
      </Button>
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" tabIndex={-1} aria-hidden="true" onChange={upload} disabled={busy} data-testid="code-mapping-upload-input" />
    </>
  );
};

export default function CodeMappingsPage() {
  const { country } = useCountry();
  const { user } = useAuth();
  const skus = useSkuOptions(country);
  const [searchParams] = useSearchParams();
  const initialSource = ['sales', 'returns', 'po'].includes(searchParams.get('source')) ? searchParams.get('source') : '';
  const [rows, setRows] = useState([]);
  const [queueVersion, setQueueVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const canEdit = canEditModule(user, 'sku_aliases');
  const canDelete = canDoAdminAction(user, 'sku_alias_delete');
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // mapping awaiting delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(`${API}/sku-aliases`, { params: { country }, headers: getAuthHeaders() });
      setRows(res.data.aliases || []);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => { load(); }, [load]);

  // Source filter (Master file, Sales upload, PO auto-match, Manual, ...), applied before search/sort/paging.
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const sourceOptions = useMemo(() => [...new Set(rows.map((r) => sourceLabel(r.source)))].sort((a, b) => a.localeCompare(b, 'en-IN')), [rows]);
  const [recentOnly, setRecentOnly] = useState(false);
  const sourceRows = useMemo(() => {
    const since = Date.now() - RECENT_MS;
    return rows.filter((r) => (sourceFilter === 'ALL' || sourceLabel(r.source) === sourceFilter)
      && (!recentOnly || (r.created_at && new Date(r.created_at).getTime() >= since) || (r.updated_at && new Date(r.updated_at).getTime() >= since)));
  }, [rows, sourceFilter, recentOnly]);
  useEffect(() => { if (sourceFilter !== 'ALL' && !sourceOptions.includes(sourceFilter)) setSourceFilter('ALL'); }, [sourceFilter, sourceOptions]);

  const table = useTableControls(sourceRows, { searchKeys: SEARCH_KEYS, initialSort: INITIAL_SORT, pageSize: 50, sortValue: SORT_VALUE });

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadFile(`${API}/sku-aliases/template`, 'code_mapping_template.csv');
    } catch (err) {
      toast.error('Could not download template', { description: errorMessage(err) });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const openDialog = (row) => { setEditing(row); setDialogOpen(true); };
  const remove = (row) => { setDeleteTarget(row); setDeleteOpen(true); };
  const confirmRemove = async () => {
    const row = deleteTarget;
    try {
      await axios.delete(`${API}/sku-aliases/${row.id}`, { headers: getAuthHeaders() });
      toast.success(`Mapping ${row.alias} → ${row.sku_code} deleted`);
      load();
    } catch (err) {
      toast.error('Could not delete mapping', { description: errorMessage(err) });
      throw err; // keep the dialog open
    }
  };

  return (
    <div className="space-y-6" data-testid="code-mappings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl flex items-center gap-2"><Link2 className="h-7 w-7" aria-hidden="true" />Code Mappings</h1>
          <p className="mt-1 text-[13px] text-gray-500">Every marketplace or file code that points to a master SKU or bundle. Codes that match nothing wait in the queue below until mapped.</p>
          {canEdit && (
            <p className="mt-1 text-[12px] text-gray-600" data-testid="code-mapping-format-hint">
              Upload file columns: <span className="font-mono">alias</span> (the code in your files) and <span className="font-mono">sku_code</span> (master SKU). CSV or Excel.
            </p>
          )}
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadTemplate} disabled={downloadingTemplate} className="h-9 text-[13px]" data-testid="code-mapping-template">
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />{downloadingTemplate ? 'Downloading…' : 'Download template'}
            </Button>
            <UploadMappingsButton country={country} onDone={() => { load(); setQueueVersion((v) => v + 1); }} />
            <Button onClick={() => openDialog(null)} className="h-9 text-[13px]" data-testid="code-mapping-add"><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Add mapping</Button>
          </div>
        ) : (
          <span className="rounded-md bg-gray-100 px-2.5 py-1 text-[12px] font-medium text-gray-700" data-testid="code-mapping-view-only">View only</span>
        )}
      </div>
      <MappingQueue country={country} canEdit={canEdit} skus={skus} onChanged={load} initialSource={initialSource} version={queueVersion} />
      <h2 className="text-sm font-semibold text-gray-900" id="mapping-rules-title">Mapping rules</h2>
      <MatchingStatsStrip country={country} />
      <div className="flex flex-wrap items-center gap-3">
        <Input value={table.query} onChange={(e) => table.setQuery(e.target.value)} placeholder="Search code, SKU, product or person…" aria-label="Search code mappings" className="w-full sm:max-w-sm" data-testid="code-mapping-search" />
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); table.setPage(1); }}>
          <SelectTrigger className="h-9 w-full text-[13px] sm:w-44" aria-label="Filter by source" data-testid="code-mapping-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All sources</SelectItem>
            {sourceOptions.map((label) => <SelectItem key={label} value={label}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-[13px] text-gray-700"><input type="checkbox" checked={recentOnly} onChange={(e) => { setRecentOnly(e.target.checked); table.setPage(1); }} data-testid="code-mapping-recent" /> Last 7 days</label>
        <span className="text-[13px] text-gray-600" data-testid="code-mapping-count">{table.total.toLocaleString('en-IN')} of {rows.length.toLocaleString('en-IN')} mappings</span>
      </div>
      {loadError && <LoadError what="code mappings" error={loadError} onRetry={load} retrying={loading} testId="code-mapping-load-error" />}
      <div className="relative overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <SortHeader label="Code" sortKey="alias" table={table} />
              <SortHeader label="Maps to" sortKey="sku_code" table={table} />
              <SortHeader label="Source" sortKey="source" table={table} />
              <SortHeader label="Added" sortKey="created_at" table={table} />
              <SortHeader label="Last edited" sortKey="updated_at" table={table} />
              <th className="px-3 py-2" scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {table.pageRows.map((row) => <MappingRow key={row.id} row={row} canEdit={canEdit} canDelete={canDelete} onEdit={openDialog} onDelete={remove} />)}
          </tbody>
        </table>
        {!loading && !loadError && !table.total && (
          <p className="p-8 text-center text-[13px] text-gray-500" data-testid="code-mapping-empty">
            {rows.length ? 'No mappings match your search or source filter.' : 'No code mappings yet.'}
          </p>
        )}
        {loading && <p role="status" className="p-8 text-center text-[13px] text-gray-500">Loading mappings…</p>}
        <TablePager table={table} itemLabel="mappings" className="border-t" />
      </div>
      {canEdit && <MappingDialog mapping={editing} open={dialogOpen} onOpenChange={setDialogOpen} country={country} skus={skus} onSaved={load} />}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete mapping ${deleteTarget?.alias || ''} → ${deleteTarget?.sku_code || ''}?`}
        description={`Future uploads containing ${deleteTarget?.alias || 'this code'} will no longer match ${deleteTarget?.sku_code || 'the SKU'} automatically. Data already imported is not changed.`}
        confirmLabel="Delete mapping"
        busyLabel="Deleting…"
        destructive
        onConfirm={confirmRemove}
        testId="delete-code-mapping-confirm"
      />
    </div>
  );
}
